import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ColumnMode, DatatableComponent } from '@swimlane/ngx-datatable';
import Swal from 'sweetalert2';
import { FechamentoService } from 'app/services/fechamento.service';

// tb_venda.id_situacao_venda (status do Efí) -> rótulo e cor do badge
const SITUACOES: { [codigo: number]: [string, string] } = {
	0: ['Cancelado', 'danger'],
	1: ['Novo', 'secondary'],
	2: ['Aguardando pagamento', 'info'],
	3: ['Pago', 'success'],
	4: ['Não pago', 'danger'],
	5: ['Expirado', 'warning'],
	6: ['Liquidado', 'success'],
	7: ['Identificado', 'info'],
	8: ['Aprovado', 'success'],
	9: ['Devolvido', 'secondary'],
	10: ['Contestado', 'danger'],
};
const SITUACOES_PAGAS = [3, 6, 8];
const EMPRESA_WHATSAPP = {
	2: 'Rastreei Sistema de Monitoramento e Telemetria Veicular',
	3: 'Faleflex',
};

@Component({
	selector: 'app-vendas-fechamento',
	templateUrl: './vendas-fechamento.component.html',
	styleUrls: ['./vendas-fechamento.component.scss'],
})
export class VendasFechamentoComponent implements OnInit {
	constructor(private route: ActivatedRoute, private fechamentoService: FechamentoService) { }

	contentHeader: object;
	ColumnMode = ColumnMode;
	idFechamento: number;
	sistema = 2;
	carregando = false;
	erro: string = null;
	vigencia = '';

	todos: any[] = [];
	rows: any[] = [];
	semCobranca: any[] = [];
	indicadores = { habilitados: 0, gerados: 0, naoGerados: 0, pagos: 0, pendencias: 0, canceladas: 0 };
	statusDisponiveis: string[] = [];

	filtroTexto = '';
	filtroStatus = '';
	filtroIndicador = '';

	atualizandoTodos = false;
	atualizando: { [idVenda: number]: boolean } = {};
	compartilhando: { [idVenda: number]: boolean } = {};
	reenviando: { [idVenda: number]: boolean } = {};

	@ViewChild(DatatableComponent) table: DatatableComponent;

	ngOnInit(): void {
		this.idFechamento = this.route.snapshot.params.id_fechamento;
		// ?sistema=3 abre direto na aba FaleFlex (link do BI Financeiro).
		if (Number(this.route.snapshot.queryParams.sistema) === 3) {
			this.sistema = 3;
		}
		this.contentHeader = {
			headerTitle: 'Vendas e Boletos',
			actionButton: false,
			breadcrumb: {
				type: '',
				links: [
					{ name: 'Início', isLink: false },
					{ name: 'Fechamento', isLink: true, link: '/fechamento' },
					{ name: 'Vendas e Boletos', isLink: false },
				],
			},
		};
		this.carregar();
	}

	trocarSistema(sistema: number) {
		if (this.sistema === sistema) {
			return;
		}
		this.sistema = sistema;
		this.filtroTexto = '';
		this.filtroStatus = '';
		this.filtroIndicador = '';
		this.carregar();
	}

	carregar() {
		this.carregando = true;
		this.erro = null;
		this.fechamentoService.getPainelVendas(this.idFechamento, this.sistema).subscribe(
			(res) => {
				const painel = res.data;
				this.vigencia = `${String(painel.fechamento.mes_vigente).padStart(2, '0')}/${painel.fechamento.ano_vigente}`;
				this.todos = painel.clientes.map((c) => ({ ...c, ...this.status(c), pendente: this.temPendencia(c) }));
				this.semCobranca = painel.sem_cobranca;
				this.statusDisponiveis = [...new Set(this.todos.map((r) => r.statusLabel))].sort();
				this.calcularIndicadores();
				this.aplicarFiltros();
				this.carregando = false;
			},
			(error) => {
				console.log(error);
				this.erro = this.mensagemErro(error, 'Não foi possível carregar as vendas do fechamento.');
				this.todos = [];
				this.rows = [];
				this.semCobranca = [];
				this.carregando = false;
			},
		);
	}

	private status(row): { statusLabel: string, statusCor: string } {
		if (!row.gerado) {
			return { statusLabel: 'Não gerado', statusCor: 'secondary' };
		}
		if (row.cancelada) {
			return { statusLabel: SITUACOES[0][0], statusCor: SITUACOES[0][1] };
		}
		if (row.etapas.some((e) => e.nome === 'Boleto' && e.status === 'erro')) {
			return { statusLabel: 'Erro no boleto', statusCor: 'danger' };
		}
		if (!row.tem_cobranca) {
			return { statusLabel: 'Sem cobrança no Efí', statusCor: 'danger' };
		}
		if (!row.tem_boleto) {
			// Cobrança criada, sem erro registrado e sem confirmação do Efí (ex.: FaleFlex, que não recebe o webhook).
			return { statusLabel: 'Status não confirmado', statusCor: 'secondary' };
		}
		const [statusLabel, statusCor] = SITUACOES[row.id_situacao_venda] || ['Sem status', 'secondary'];
		return { statusLabel, statusCor };
	}

	private temPendencia(row): boolean {
		return this.ativa(row) && (!row.tem_cobranca || row.qtd_vendas_ativas > 1 || row.etapas.some((e) => e.status === 'erro'));
	}

	private ativa(row): boolean {
		return row.gerado && !row.cancelada;
	}

	// A tabela tem uma linha por venda (inclusive canceladas): os indicadores de cliente contam clientes distintos.
	private calcularIndicadores() {
		const clientes = (filtro: (r) => boolean) => new Set(this.todos.filter(filtro).map((r) => r.id_cliente)).size;
		const gerados = clientes((r) => this.ativa(r));
		const naoGerados = clientes((r) => !r.gerado);
		this.indicadores = {
			habilitados: gerados + naoGerados,
			gerados,
			naoGerados,
			pagos: clientes((r) => this.ativa(r) && SITUACOES_PAGAS.includes(r.id_situacao_venda)),
			pendencias: this.todos.filter((r) => r.pendente).length,
			canceladas: this.todos.filter((r) => r.cancelada).length,
		};
	}

	filtrarIndicador(chave: string) {
		this.filtroIndicador = this.filtroIndicador === chave ? '' : chave;
		this.aplicarFiltros();
	}

	aplicarFiltros() {
		const texto = this.filtroTexto.trim().toLowerCase();
		const digitos = texto.replace(/\D/g, '');
		this.rows = this.todos.filter((r) => {
			if (texto) {
				const nome = String(r.cliente || '').toLowerCase().includes(texto);
				const documento = digitos && String(r.cpf_cnpj || '').replace(/\D/g, '').includes(digitos);
				if (!nome && !documento) {
					return false;
				}
			}
			if (this.filtroStatus && r.statusLabel !== this.filtroStatus) {
				return false;
			}
			switch (this.filtroIndicador) {
				case 'gerados': return this.ativa(r);
				case 'naoGerados': return !r.gerado;
				case 'pagos': return this.ativa(r) && SITUACOES_PAGAS.includes(r.id_situacao_venda);
				case 'pendencias': return r.pendente;
				case 'canceladas': return r.cancelada;
				default: return true;
			}
		});
	}

	limparFiltros() {
		this.filtroTexto = '';
		this.filtroStatus = '';
		this.filtroIndicador = '';
		this.aplicarFiltros();
	}

	alternarEtapas(row) {
		if (row.gerado) {
			this.table.rowDetail.toggleExpandRow(row);
		}
	}

	podeCompartilhar(row): boolean {
		return this.ativa(row) && row.tem_cobranca && !SITUACOES_PAGAS.includes(row.id_situacao_venda);
	}

	dicaEtapa(etapa): string {
		if (etapa.status === 'erro') {
			return `${etapa.nome}: erro - ${etapa.mensagem || 'sem mensagem'}`;
		}
		if (etapa.status === 'ok') {
			return `${etapa.nome}: concluída${etapa.data ? ' em ' + new Date(etapa.data).toLocaleString('pt-BR') : ''}`;
		}
		if (etapa.status === 'sem_registro') {
			return `${etapa.nome}: sem registro (venda criada antes do controle de etapas)`;
		}
		return `${etapa.nome}: não executada`;
	}

	// O que a tela oferece para a etapa com erro: refazer aqui, ou a orientação de onde refazer.
	acaoEtapa(row, etapa): string {
		if (etapa.status !== 'erro' || row.cancelada) {
			return null;
		}
		switch (etapa.nome) {
			case 'Gestão Click': return this.sistema === 3 ? 'gestao_click' : 'gestao_click_rastreei';
			case 'E-mail': return 'email';
			case 'Relatórios':
			case 'Arquivos no S3': return 'crawler';
			case 'Boleto': return 'boleto';
			default: return null;
		}
	}

	reenviarGestaoClick(row) {
		Swal.fire({
			title: 'Reenviar ao Gestão Click',
			text: `Criar a venda de ${row.cliente} no Gestão Click? A cobrança, o boleto e o e-mail não mudam.`,
			icon: 'question',
			showCancelButton: true,
			confirmButtonColor: '#161D31',
			cancelButtonColor: '#6e7d88',
			confirmButtonText: 'Reenviar',
			cancelButtonText: 'Cancelar',
		}).then((resultado) => {
			if (!resultado.isConfirmed) {
				return;
			}
			this.reenviando[row.id_venda] = true;
			this.fechamentoService.postReenviarGestaoClick(row.id_venda).subscribe(
				(res) => {
					this.reenviando[row.id_venda] = false;
					const r = res.data;
					const situacaoComErro = String(r.situacao_gestao_click || '').startsWith('erro');
					this.aviso(situacaoComErro ? 'warning' : 'success', situacaoComErro
						? `Venda criada no Gestão Click (${r.id_venda_externo}), mas a situação não foi atualizada: ${r.situacao_gestao_click}`
						: `Venda de ${row.cliente} criada no Gestão Click (${r.id_venda_externo})`);
					this.carregar();
				},
				(error) => {
					this.reenviando[row.id_venda] = false;
					this.aviso('error', this.mensagemErro(error, 'Não foi possível reenviar ao Gestão Click.'));
					this.carregar();
				},
			);
		});
	}

	atualizarStatus(row) {
		this.atualizando[row.id_venda] = true;
		this.fechamentoService.postAtualizarStatusVenda(row.id_venda).subscribe(
			(res) => {
				this.atualizando[row.id_venda] = false;
				const r = res.data;
				let titulo = r.mensagem
					|| (r.alterado
						? `${row.cliente}: ${SITUACOES[r.situacao_anterior]?.[0] || 'sem status'} → ${SITUACOES[r.id_situacao_venda][0]}`
						: `${row.cliente}: status sem mudança (${SITUACOES[r.id_situacao_venda][0]})`);
				if (r.gestao_click && String(r.gestao_click).startsWith('erro')) {
					titulo += `. Gestão Click: ${r.gestao_click}`;
				}
				this.aviso(r.mensagem ? 'warning' : 'success', titulo);
				this.carregar();
			},
			(error) => {
				this.atualizando[row.id_venda] = false;
				this.aviso('error', this.mensagemErro(error, 'Não foi possível consultar o status no Efí.'));
			},
		);
	}

	atualizarTodos() {
		const quantidade = this.todos.filter((r) => this.ativa(r) && r.tem_cobranca).length;
		Swal.fire({
			title: 'Atualizar status no Efí',
			text: `Consultar no Efí o status das ${quantidade} venda(s) não canceladas desta aba e atualizar o Gestão Click?`,
			icon: 'question',
			showCancelButton: true,
			confirmButtonColor: '#161D31',
			cancelButtonColor: '#6e7d88',
			confirmButtonText: 'Atualizar',
			cancelButtonText: 'Cancelar',
		}).then((resultado) => {
			if (!resultado.isConfirmed) {
				return;
			}
			this.atualizandoTodos = true;
			this.fechamentoService.postAtualizarStatusFechamento(this.idFechamento, this.sistema).subscribe(
				(res) => {
					this.atualizandoTodos = false;
					const r = res.data;
					Swal.fire({
						title: 'Status atualizados',
						text: `${r.consultadas} consultada(s), ${r.alteradas} com status alterado, ${r.erros} com erro.`,
						icon: r.erros ? 'warning' : 'success',
						confirmButtonColor: '#161D31',
					});
					this.carregar();
				},
				(error) => {
					this.atualizandoTodos = false;
					this.aviso('error', this.mensagemErro(error, 'Não foi possível atualizar os status.'));
				},
			);
		});
	}

	compartilharEmail(row) {
		Swal.fire({
			title: 'Confirmação',
			text: `Reenviar o boleto de ${row.cliente} por e-mail?`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#161D31',
			cancelButtonColor: '#6e7d88',
			confirmButtonText: 'Confirmar',
			cancelButtonText: 'Cancelar',
		}).then((resultado) => {
			if (!resultado.isConfirmed) {
				return;
			}
			this.compartilhando[row.id_venda] = true;
			const envio = this.sistema === 3
				? this.fechamentoService.postEnviarBoletoFaleflex(row.id_cliente, this.idFechamento)
				: this.fechamentoService.postEnviarBoleto(row.id_cliente, this.idFechamento);
			envio.subscribe(
				() => {
					this.compartilhando[row.id_venda] = false;
					this.aviso('success', `Boleto de ${row.cliente} reenviado por e-mail`);
					this.carregar();
				},
				(error) => {
					this.compartilhando[row.id_venda] = false;
					this.aviso('error', this.mensagemErro(error, 'Não foi possível reenviar o boleto.'));
				},
			);
		});
	}

	compartilharWhatsapp(row) {
		this.compartilhando[row.id_venda] = true;
		this.fechamentoService.postWhatsappVenda(row.id_venda).subscribe(
			(res) => {
				this.compartilhando[row.id_venda] = false;
				const d = res.data;
				const valor = d.total != null
					? Number(d.total).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
					: '';
				const vencimento = d.data_vencimento ? d.data_vencimento.split('-').reverse().join('/') : '';
				const pix = d.banking_billet.pix ? ` ou pague usando o Pix Copia e Cola: ${d.banking_billet.pix}` : '';
				const mensagem = `Olá, segue o boleto no valor de *R$ ${valor}*. Emitido por ${EMPRESA_WHATSAPP[this.sistema]}, `
					+ `com vencimento para *${vencimento}*. Acesse o boleto pelo link: ${d.banking_billet.pdf}${pix}`;
				const telefone = String(d.telefone_financeiro || '').replace(/\D/g, '');
				const destino = telefone ? `https://wa.me/55${telefone}` : 'https://wa.me/';
				window.open(`${destino}?text=${encodeURIComponent(mensagem)}`, '_blank');
			},
			(error) => {
				this.compartilhando[row.id_venda] = false;
				this.aviso('error', this.mensagemErro(error, 'Não foi possível montar a mensagem do WhatsApp.'));
			},
		);
	}

	private aviso(icon: 'success' | 'warning' | 'error', title: string) {
		Swal.fire({ toast: true, position: 'top-end', icon, title, showConfirmButton: false, timer: 4000, timerProgressBar: true });
	}

	private mensagemErro(error, padrao: string): string {
		const mensagem = error?.error?.message;
		if (error?.status === 401) {
			return 'Sessão expirada. Faça login novamente.';
		}
		return Array.isArray(mensagem) ? mensagem.join('; ') : (mensagem || padrao);
	}
}
